<div class="container mx-auto max-w-2xl space-y-6 px-4 py-8">
  <header class="text-center">
    <h1 hlmH3>Gutschein einlösen</h1>
    <p hlmMuted class="mt-1">
      @if (voucher) {
        Betrag eingeben, prüfen, einlösen
      } @else {
        QR-Code scannen oder Gutscheincode eingeben
      }
    </p>
  </header>

  @if (isNetwork()) {
    <div hlmAlert>
      <ng-icon name="lucideUsers" />
      <p hlmAlertTitle>Einlösen machen deine Mitglieder</p>
      <p hlmAlertDescription>
        Als Netzwerk verkaufst du Gutscheine, eingelöst werden sie bei deinen Mitgliedern. Jedes
        Mitglied löst in seinem eigenen Zugang ein.
      </p>
      <a
        hlmBtn
        variant="outline"
        size="sm"
        class="mt-2"
        [routerLink]="['/settings']"
        [queryParams]="{ tab: 'verbund-members' }">
        Zu den Mitgliedern
      </a>
    </div>
  } @else if (!voucher) {
    <!-- ═══════════ Scan / input box ═══════════
       Both modes render into the same square, so switching does not shift
       anything below. Once a voucher is found the box makes room for it. -->
    @if (isLoading) {
      <div
        class="bg-muted border-border text-muted-foreground mx-auto flex aspect-square w-full max-w-sm flex-col items-center justify-center gap-3 rounded-xl border">
        <hlm-spinner class="text-[length:--spacing(8)]" />
        <p class="text-sm">Gutschein wird gesucht…</p>
      </div>
    } @else {
      <hlm-tabs [tab]="mode" class="items-center">
        <div hlmTabsContent="scan" class="w-full">
          <div
            class="bg-muted border-border relative mx-auto aspect-square w-full max-w-sm overflow-hidden rounded-xl border">
            <video
              #videoElement
              class="size-full object-cover"
              [class.hidden]="!isScanning"></video>

            @if (!isScanning) {
              <div
                class="text-muted-foreground absolute inset-0 flex flex-col items-center justify-center gap-3">
                <hlm-spinner class="text-[length:--spacing(8)]" />
                <p class="text-sm">Kamera wird gestartet…</p>
              </div>
            } @else {
              <!-- Viewfinder -->
              <div
                class="pointer-events-none absolute inset-6 rounded-lg border-2 border-white/80"></div>
            }
          </div>
          <p hlmMuted class="mt-3 text-center text-sm">
            Positioniere den QR-Code mittig im Rahmen.
          </p>
        </div>

        <div hlmTabsContent="code" class="w-full">
          <form
            [formGroup]="voucherForm"
            (ngSubmit)="lookupVoucher()"
            class="bg-muted border-border mx-auto flex aspect-square w-full max-w-sm flex-col items-center justify-center gap-3 rounded-xl border px-6">
            <ng-icon
              name="lucideTicket"
              class="text-muted-foreground text-[length:--spacing(10)]" />

            <div hlmField class="w-full">
              <label hlmFieldLabel for="code" class="sr-only">Gutscheincode</label>
              <input
                hlmInput
                id="code"
                type="text"
                formControlName="code"
                placeholder="Gutscheincode"
                autocomplete="off"
                class="w-full text-center font-mono uppercase"
                [attr.aria-invalid]="
                  voucherForm.get('code')?.invalid && voucherForm.get('code')?.touched
                    ? 'true'
                    : null
                " />
            </div>

            <button hlmBtn type="submit" class="w-full" [disabled]="voucherForm.invalid">
              Gutschein suchen
            </button>
          </form>
          <p hlmMuted class="mt-3 text-center text-sm">Der Code steht auf dem Gutschein.</p>
        </div>

        <!-- Placed after the content so the tab bar sits below the box -->
        <hlm-tabs-list class="mx-auto w-fit">
          <button hlmTabsTrigger="scan" (click)="setMode('scan')">
            <ng-icon name="lucideScanLine" />
            QR-Code scannen
          </button>
          <button hlmTabsTrigger="code" (click)="setMode('code')">
            <ng-icon name="lucideKeyboard" />
            Gutscheincode eingeben
          </button>
        </hlm-tabs-list>
      </hlm-tabs>
    }

    @if (error) {
      <div hlmAlert variant="destructive">
        <ng-icon name="lucideTriangleAlert" />
        <p hlmAlertTitle>{{ getErrorTitle() }}</p>
        <p hlmAlertDescription>{{ error }}</p>
        <div hlmAlertAction>
          <button hlmBtn variant="ghost" size="icon-sm" aria-label="Schließen" (click)="error = ''">
            <ng-icon name="lucideX" />
          </button>
        </div>
      </div>
    }
  } @else {
    <!-- ═══════════ Voucher found ═══════════ -->
    <button hlmBtn variant="ghost" size="sm" class="-ml-2" (click)="startOver()">
      <ng-icon name="lucideArrowLeft" />
      Anderer Gutschein
    </button>

    <app-voucher-balance-card
      [code]="voucher.code"
      [subtitle]="voucherSubtitle"
      [status]="voucher.status"
      [amount]="voucher.amount"
      [initialAmount]="voucher.initial_amount"
      [product]="voucher.voucher_product_data">
      @if (isFranchiseVoucher) {
        <div hlmAlert>
          <ng-icon name="lucideUsers" />
          <p hlmAlertTitle>Netzwerk-Gutschein</p>
          <p hlmAlertDescription>
            Dieser Gutschein wurde von deinem Netzwerk verkauft. Den eingelösten Betrag bekommst du
            mit der nächsten Monatsabrechnung überwiesen.
          </p>
        </div>
      }
    </app-voucher-balance-card>

    @if (showRedemption) {
      <app-redemption-flow
        [code]="voucher.code"
        [balance]="voucher.amount"
        [busy]="isRedeeming"
        [error]="redemptionError"
        [result]="redemptionResult"
        (confirmed)="redeem($event)"
        (continued)="continueRedemption()"
        (errorDismissed)="redemptionError = ''">
        @if (isFranchiseVoucher) {
          <p resultNote hlmMuted class="text-center text-sm">
            Den Betrag bekommst du mit der nächsten Monatsabrechnung überwiesen.
          </p>
        }
        <button resultActions hlmBtn size="lg" class="w-full" (click)="startOver()">
          <ng-icon name="lucideScanLine" />
          Nächsten Gutschein einlösen
        </button>
      </app-redemption-flow>
    }

    <app-voucher-timeline
      [createdAt]="voucher.created_at"
      [expiresAt]="voucher.expires_at"
      [redemptions]="voucher.redemptions" />
  }
</div>
